import lockup from '@assets/brand/plug-lockup-white.svg'
import { authMethods, useConnect } from '@hooks/useConnect'
import { Ed25519KeyIdentity } from '@icp-sdk/core/identity'
import { useConnectionProvider } from '@plug-core/state'
import { ConnectionProvider } from '@plug-core/state/connectionProvider'
import {
  Chip,
  Heading,
  ListGroup,
  ListRow,
  PField,
  PFieldState,
  PlugMark,
  RowTile,
  Spinner,
} from '@ui'
import { setAuthClient } from '@utils/internetIdentity'
import { WALLET_SETUP_ROUTES } from '@utils/walletSetupRoutes'
import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiPlus } from 'react-icons/hi'
import { useNavigate } from 'react-router-dom'
import { useLaunchChoreography } from '../useLaunchChoreography'
import '../welcome.css'

type AuthMethodKey = keyof typeof authMethods

interface AuthRowProps {
  onClick: () => void
  isLoading: boolean
  disabled: boolean
  chip?: string
  label: string
  icon: ReactNode
}

const AuthRow = ({ onClick, isLoading, disabled, chip, label, icon }: AuthRowProps) => (
  <ListRow
    onClick={onClick}
    // The row being connected stays at full strength; the others dim while it is busy.
    disabled={disabled || isLoading}
    className={isLoading ? 'cursor-progress opacity-100' : undefined}
    aria-busy={isLoading || undefined}
    leading={<RowTile>{icon}</RowTile>}
    primary={label}
    trailing={
      isLoading ? <Spinner size={20} /> : chip ? <Chip tone="accent">{chip}</Chip> : undefined
    }
  />
)

const methodIcon = (method: AuthMethodKey) =>
  method === 'plug' ? (
    <PlugMark variant="auto" size={26} title="" />
  ) : (
    <img src={authMethods[method].icon} alt="" />
  )

interface SignInOptionsProps {
  onConnect: (provider: ConnectionProvider) => void
  createWallet: () => void
  connectingProvider: ConnectionProvider
}

const SignInOptions = ({ onConnect, createWallet, connectingProvider }: SignInOptionsProps) => {
  const [showOptions, setShowOptions] = useState(false)

  const { t } = useTranslation()
  const { previousProvider } = useConnectionProvider()
  const navigate = useNavigate()

  useEffect(() => {
    // on mobile safari you can't have async calls before authClient.login
    // as that will block II auth popup.
    // Since AuthClient.create is async method, we're calling it here instead.
    setAuthClient(Ed25519KeyIdentity.generate())
  }, [])

  const returning = !!previousProvider && !!authMethods[previousProvider]
  const busy = !!connectingProvider
  const lastUsed = t('welcome.lastUsed')

  const row = (method: AuthMethodKey, onClick: () => void, chip?: string) => (
    <AuthRow
      key={method}
      onClick={onClick}
      chip={chip}
      isLoading={connectingProvider === method}
      disabled={busy}
      label={authMethods[method].title}
      icon={methodIcon(method)}
    />
  )

  return (
    <>
      <Heading level={3} className="welcome__heading">
        {previousProvider ? t('welcome.welcomeBack') : t('welcome.signIn')}
      </Heading>

      {returning && !showOptions && (
        <>
          <ListGroup>
            {row(previousProvider as AuthMethodKey, () => onConnect(previousProvider), lastUsed)}
          </ListGroup>

          <button type="button" className="welcome__more" onClick={() => setShowOptions(true)}>
            {t('welcome.moreOptions')}
          </button>
        </>
      )}

      {(!previousProvider || showOptions) && (
        <ListGroup>
          {row(
            'plug',
            () => navigate(WALLET_SETUP_ROUTES.CONNECT_PLUG),
            !previousProvider
              ? t('welcome.recommended')
              : previousProvider === 'plug'
                ? lastUsed
                : undefined
          )}
          {row(
            'internet-identity-v2',
            () => onConnect('internet-identity-v2'),
            previousProvider === 'internet-identity-v2' ? lastUsed : undefined
          )}
          {row(
            'internet-identity-legacy',
            () => onConnect('internet-identity-legacy'),
            previousProvider === 'internet-identity-legacy' ? lastUsed : undefined
          )}
          {row('oisy', () => onConnect('oisy'), previousProvider === 'oisy' ? lastUsed : undefined)}
          <AuthRow
            onClick={createWallet}
            isLoading={false}
            disabled={busy}
            label={t('welcome.create')}
            icon={<HiPlus aria-hidden="true" className="size-5" />}
          />
        </ListGroup>
      )}
    </>
  )
}

export const WalletSetupOptions = () => {
  const { t } = useTranslation()
  const { onConnect, createWallet, connectingProvider } = useConnect()
  const phase = useLaunchChoreography()

  // While a sign-in popup is open the field listens and the mark holds still (plan C5).
  const waiting = !!connectingProvider
  const fieldState: PFieldState = waiting
    ? 'listen'
    : phase === 'enter'
      ? 'none'
      : phase === 'ripple'
        ? 'ripple'
        : 'breathe'

  return (
    <div className="welcome">
      <header className="welcome__header">
        <img src={lockup} alt="Plug" className="welcome__lockup" />
      </header>

      <div className="welcome__body">
        <div className="welcome__stage" aria-hidden="true">
          <PField
            className="welcome__field"
            state={fieldState}
            phase={fieldState === 'breathe' ? -3000 : 0}
            mark={{
              variant: 'auto',
              motion: waiting ? 'still' : 'float',
              enter: phase !== 'idle',
            }}
          />
        </div>

        <div className="welcome__copy">
          <h1 className="welcome__headline">
            <span>{t('welcome.taglineLine1')}</span>
            <span>{t('welcome.taglineLine2')}</span>
          </h1>
          <p className="welcome__lead">{t('welcome.lead')}</p>

          <SignInOptions
            onConnect={onConnect}
            createWallet={createWallet}
            connectingProvider={connectingProvider}
          />
        </div>
      </div>
    </div>
  )
}
